export const INTEGRATION_GROUPS = [
  'contracts',
  'payment',
  'accounting',
  'distribution',
  'metering',
  'debtCollection',
  'stairwellDisplays',
  'maintenance',
  'rental',
  'other',
] as const;

export type IntegrationGroup = (typeof INTEGRATION_GROUPS)[number];

export const GROUP_LABELS: Record<IntegrationGroup, string> = {
  contracts: 'Avtal',
  payment: 'Betalning',
  accounting: 'Bokföring',
  distribution: 'Distribution',
  metering: 'IMD',
  debtCollection: 'Inkasso',
  stairwellDisplays: 'Trapphustavlor',
  maintenance: 'Underhåll',
  rental: 'Uthyrning',
  other: 'Övrigt',
};

// Primary source: each service's `category` (a stable, environment-independent
// identifier on the manifest). Extend this as new categories appear.
const CATEGORY_TO_GROUP: Record<string, IntegrationGroup> = {
  payments_collection: 'payment',
  bookkeeping_sync: 'accounting',
  debt_invoice_enforcement: 'debtCollection',
  imd_data_collection: 'metering',
  e_signing: 'contracts',
  e_signing_document_building: 'contracts',
  message_distribution: 'distribution',
  message_e_delivery_postal_distribution: 'distribution',
  email_distribution: 'distribution',
  message_sms_distribution: 'distribution',
  public_data: 'other',
  rental_marketplace: 'rental',
};

// Fallback for integrations whose service has no (mappable) `category`. Keep
// this list minimal — prefer having the backend set `category` instead. The
// long-term fix is to populate `category` on every service so this map can be
// dropped entirely.
const APPLICATION_ID_TO_GROUP: Record<string, IntegrationGroup> = {
  '4326fe75-d324-4c94-ab06-03e4aeb11226': 'metering', // EcoGuard
  'caa7c4fd-5b46-49be-8abf-10f44c1abdaa': 'metering', // Loggamera
  '8cf6b2f4-4084-4368-9483-fa59e4fc9845': 'maintenance', // Planima
  '5e01538f-a1fa-45ba-9f9d-67fb9617d984': 'stairwellDisplays', // embsign
  '91bd8f0a-5b0d-4d0e-9b42-695fbfb360bd': 'stairwellDisplays', // Signcast
  '6be9f778-2713-4e5f-a80e-f8db86725b89': 'rental', // HomeQ
  'fabe15b5-11ee-45f6-8263-c623059babc4': 'rental', // Uppsala bostadsförmedling
};

export function getIntegrationVariantName(
  companyName: string,
  servicePublicName: string | undefined
): string | null {
  if (!servicePublicName) return null;
  return servicePublicName.trim().toLowerCase() ===
    companyName.trim().toLowerCase()
    ? null
    : servicePublicName;
}

// Resolve a service to a group: category first, then an application_id
// override, otherwise 'other'.
export function getIntegrationGroup(
  applicationId: string,
  category: string | undefined
): IntegrationGroup {
  if (category && category in CATEGORY_TO_GROUP) {
    return CATEGORY_TO_GROUP[category];
  }
  return APPLICATION_ID_TO_GROUP[applicationId] ?? 'other';
}
